import { createSelector, createSlice } from "@reduxjs/toolkit";
import {
  createNewView,
  getDataViews,
  saveCurrentView,
} from "../../actions/dataTable/DataSaveViewsAsyncActions";
import { RootState } from "../../../configureStore";

export type DataViewsState = {
  dataViews: any;
  loading: boolean;
  error: string | null;
  tableConfiguration?: any;
  isCreatingView: boolean;
  saveViewSuccess: boolean;
  saveViewError: string | null;
  savedViews: any[];
};

export const initialState: DataViewsState = {
  dataViews: {},
  loading: false,
  error: null,
  tableConfiguration: null,
  isCreatingView: false,
  saveViewSuccess: false,
  saveViewError: null,
  savedViews: [],
};

const DataViewsSlice = createSlice({
  name: "DataViews",
  initialState,
  reducers: {
    setTableConfiguration(state, action) {
      state.tableConfiguration = action.payload;
    },
    setDataViews(state, action) {
      state.dataViews = action.payload;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    setSavedViews(state, action) {
      state.savedViews = action.payload;
    },
    resetSaveViewState(state) {
      state.isCreatingView = false;
      state.saveViewSuccess = false;
      state.saveViewError = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(createNewView.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNewView.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        if (state.dataViews?.data && Array.isArray(state.dataViews.data)) {
          state.dataViews.data.push(action.payload);
        }
      })
      .addCase(createNewView.rejected, (state, action) => {
        state.loading = false;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Unknown error occurred";
      })

      .addCase(getDataViews.pending, (state) => {
        state.loading = true;
      })

      .addCase(getDataViews.fulfilled, (state, action) => {
        state.dataViews = action.payload;
      })

      .addCase(getDataViews.rejected, (state, action) => {
        state.loading = false;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Unknown error occurred";
      })

      .addCase(saveCurrentView.pending, (state) => {
        state.isCreatingView = true;
        state.saveViewSuccess = false;
        state.saveViewError = null;
      })
      .addCase(saveCurrentView.fulfilled, (state, action) => {
        state.isCreatingView = false;
        state.saveViewSuccess = true;
        state.saveViewError = null;

        if (action.payload?.updatedViews) {
          state.dataViews = action.payload.updatedViews;

          if (action.payload.updatedViews?.data) {
            state.savedViews = action.payload.updatedViews.data.map(
              (views: any) => ({
                id: views.id,
                name: views.name,
                savedDate: new Date(views.createdAt).toLocaleDateString(),
                filters: views.config.filters,
                configuration: views.config.table,
                createdAt: views.createdAt,
                dateFilter: views.config.date || null,
              })
            );
          }
        }
      })
      .addCase(saveCurrentView.rejected, (state, action) => {
        state.isCreatingView = false;
        state.saveViewSuccess = false;
        state.saveViewError =
          typeof action.payload === "string"
            ? action.payload
            : "Error saving view";
      });
  },
});

export const {
  setDataViews,
  setLoading,
  setError,
  setTableConfiguration,
  setSavedViews,
  resetSaveViewState,
} = DataViewsSlice.actions;

export const DataViewsReducer = DataViewsSlice.reducer;
